import API_CONFIG from '@/config/api';
import { MFASigninResponse } from './auth.service';

/**
 * v4 SSO service.
 *
 * The legacy v1 endpoints (/authentication/v1/Social/GetLoginOptions etc.) live
 * on the global api.seliseblocks.com and are not project-scoped. On a
 * project-scoped host (e.g. https://blocksapi.slsblx.com) those endpoints
 * 404, so we use the v4 /iam/v4/idp/initiate + /iam/v4/idp/callback pattern.
 *
 * Flow:
 *   1. startLogin() — call /iam/v4/idp/initiate, get { redirect_uri }, navigate.
 *   2. User authenticates on iam.seliseblocks.com.
 *   3. IAM redirects back to VITE_BLOCKS_REDIRECT_URI with ?code=&state=.
 *   4. finishLogin() — call /iam/v4/idp/callback to set the session cookie.
 */

const CLIENT_ID = (import.meta.env.VITE_BLOCKS_OIDC_CLIENT_ID as string) ?? '';
const REDIRECT_URI =
  (import.meta.env.VITE_BLOCKS_REDIRECT_URI as string) ??
  `${window.location.origin}/login/callback`;

/**
 * Step 1: fetch the authorize URL from Blocks (do NOT navigate yet).
 * Returns the redirect URI the app should hand the browser.
 */
export async function startLogin(): Promise<void> {
  if (!CLIENT_ID) {
    throw new Error(
      'VITE_BLOCKS_OIDC_CLIENT_ID is not set — configure your OIDC client in the Blocks portal.'
    );
  }

  const qs =
    `?x-blocks-key=${encodeURIComponent(API_CONFIG.blocksKey)}` +
    `&clientId=${encodeURIComponent(CLIENT_ID)}` +
    `&redirectUri=${encodeURIComponent(REDIRECT_URI)}`;
  const url = `${API_CONFIG.baseUrl}/iam/v4/idp/initiate${qs}`;

  const res = await fetch(url, {
    headers: { 'x-blocks-key': API_CONFIG.blocksKey },
  });
  if (!res.ok) {
    throw new Error(`initiate failed: ${res.status}`);
  }
  const json = (await res.json()) as { redirect_uri?: string };
  if (!json.redirect_uri) {
    throw new Error('initiate returned no redirect_uri');
  }
  window.location.assign(json.redirect_uri);
}

/**
 * Step 4: run on the callback route. The callback sets the session cookie.
 */
export async function finishLogin(search: string): Promise<void> {
  const params = new URLSearchParams(search);
  const code = params.get('code');
  const state = params.get('state');
  if (!code || !state) {
    throw new Error('missing code/state on callback');
  }

  const url =
    `${API_CONFIG.baseUrl}/iam/v4/idp/callback` +
    `?code=${encodeURIComponent(code)}` +
    `&state=${encodeURIComponent(state)}`;

  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'x-blocks-key': API_CONFIG.blocksKey },
  });
  if (!res.ok) {
    throw new Error(`callback failed: ${res.status}`);
  }
}

/**
 * Refresh the short-lived hosted SSO session. Blocks rotates cookies on
 * success. Use as the 401-retry path: call this, then retry the original
 * request once.
 */
export async function refreshSession(refreshToken?: string): Promise<void> {
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: CLIENT_ID,
  });
  if (refreshToken) body.set('refresh_token', refreshToken);

  const res = await fetch(`${API_CONFIG.baseUrl}/iam/v4/oidc/token`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'x-blocks-key': API_CONFIG.blocksKey,
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body,
  });
  if (!res.ok) {
    throw new Error(`token refresh failed: ${res.status}`);
  }
}

/**
 * Logout: cookie/session-based, revokes and clears the session.
 */
export async function logout(): Promise<void> {
  const res = await fetch(`${API_CONFIG.baseUrl}/iam/v4/auth/Logout`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'Content-Type': 'application/json',
      'x-blocks-key': API_CONFIG.blocksKey,
    },
    body: JSON.stringify({}),
  });
  if (!res.ok) {
    throw new Error(`logout failed: ${res.status}`);
  }
}

// ---------------------------------------------------------------------------
// Legacy compatibility — re-exported so existing imports still resolve.
// ---------------------------------------------------------------------------
export interface SSOLoginResponse {
  providerUrl?: string;
  error?: string;
  requiresMfa?: boolean;
  mfaToken?: string;
  mfaType?: number;
  email?: string;
  status?: number;
}

export class SSOservice {
  async getSocialLoginEndpoint(payload: any): Promise<SSOLoginResponse> {
    // The v1 endpoint isn't available on the project-scoped host. Use startLogin() instead.
    console.warn(
      '[SSO] getSocialLoginEndpoint is deprecated on the project-scoped host — use startLogin()'
    );
    return { error: 'deprecated — use startLogin()', status: 410 };
  }

  async verifyMfaCode(_mfaToken: string, _code: string): Promise<MFASigninResponse> {
    throw new Error('MFA verification requires the OIDC flow — not implemented in v4');
  }
}

/**
 * Kept for backward compatibility with components that still call
 * useGetLoginOptions(). The new flow uses startLogin() directly from a button.
 * Returns null because v4 doesn't expose a "list of SSO providers" endpoint —
 * the providers are configured at the OIDC-client level in the portal.
 */
export const getLoginOption = async (): Promise<null> => {
  return null;
};